import styled from 'styled-components';
import tw, { css } from 'twin.macro';
import { motion } from 'framer-motion';
import { Body, Meta } from '../../styled components/Typography';

export const TopLevelItem = styled.div(({ $variant }) => [
  tw`border-l-4 border-solid last:mb-16`,

  /* Set border and background color based on the variant */
  $variant === 'actual-hour' && tw`border-orange-base`,
  ($variant === 'break' || $variant === `actual-hour-break`) &&
    tw`border-transparent bg-orange-lighten-40`,
  $variant === 'no-roster' && tw`border-transparent bg-silver-lighten-30`,
  $variant === 'unavailability' && tw`border-red-base`,
  $variant === 'outside-shift' && tw`border-transparent`,
  $variant === 'shift' && tw`border-orange-base`,

  /* Set background image based on the variant */
  $variant === 'break' &&
    `background-image: linear-gradient(to bottom, #FFA500 45%, rgba(255, 255, 255, 0) 0%);
    background-position: -4px center;
    background-size: 4px 8px;
    background-repeat: repeat-y;`,

  $variant === 'actual-hour-break' &&
    `
    background-image: linear-gradient(to bottom, #FFA500 45%, rgba(255, 255, 255, 0) 0%);
    background-position: -4px;
    background-size: 4px 8px;
    background-repeat: repeat-y;`,

  $variant === 'no-roster' &&
    `background-image: linear-gradient(to bottom, #CCCCCC 45%, rgba(255, 255, 255, 0) 0%);
    background-position: -4px;
    background-size: 4px 8px;
    background-repeat: repeat-y;`
]);

export const TopLevelItemHour = styled(Meta).attrs(() => ({
  as: 'div'
}))(({ $variant }) => [
  ($variant === 'shift' || $variant === 'break' || $variant === 'no-roster') &&
    tw`text-orange-darken-10`,
  ($variant === 'actual-hour' || $variant === 'actual-hour-break') &&
    tw`text-cyan-darken-10`,
  $variant === 'unavailability' && tw`text-red-darken-20`,
  tw`ml-8 py-4 tabular-nums`
]);

export const TopLevelItemLabel = styled(Meta)(({ $variant }) => [
  ($variant === 'shift' || $variant === 'break' || $variant === 'no-roster') &&
    tw`text-orange-darken-10`,
  ($variant === 'actual-hour' || $variant === 'actual-hour-break') &&
    tw`text-cyan-darken-10`,
  $variant === 'unavailability' && tw`text-red-darken-20`
]);

export const TopLevelItemBody = styled.div(({ $typename, $variant }) => [
  tw`ml-8 pb-8`,

  ($variant === 'actual-hour' ||
    $variant === 'break' ||
    $variant === 'actual-hour-break' ||
    $variant === 'no-roster' ||
    $variant === 'unavailability') &&
    tw`mt-8 pt-4`,

  $variant === 'outside-shift' && tw`mt-8 pt-8`,
  $variant === 'overlap' && tw`mt-16 pt-4`,

  $typename === null && tw`mt-0`
]);

/* BOOKING */
export const BookingItem = styled.div(({ $hasActuals, onClick }) => [
  tw`flex justify-between items-start py-4 px-8`,
  onClick && tw`cursor-pointer hover:bg-silver-lighten-30`,
  $hasActuals && tw`text-cyan-darken-10`
]);
export const BookingItemText = styled(Body).attrs(() => ({
  as: 'div'
}))(({ $hasActuals }) => [$hasActuals && tw`text-cyan-darken-10`]);
export const BookingItemMetaText = styled(Meta).attrs(() => ({
  as: 'div',
  $color: 'gray dark'
}))(({ $hasActuals }) => [$hasActuals && tw`text-cyan-darken-10`]);

/* SUMMARIZED BOOKING */
export const SummarizedBooking = styled.div(({ $hasActuals }) => [
  tw`flex items-stretch mb-8`,
  $hasActuals && tw`text-cyan-darken-10`
]);
export const SummarizedBookingTimes = styled.div(() => [
  tw`relative text-right mr-8 tabular-nums shrink-0`
]);
export const SummarizedBookingDivider = styled.span(({ $hasActuals }) => [
  tw`absolute top-0 right-0 -mr-8 w-px h-full z-0`,
  $hasActuals ? tw`bg-cyan-base` : tw`bg-silver-lighten-10`
]);
export const SummarizedBookingStartHour = styled(Body).attrs(() => ({
  as: 'div'
}))(({ $hasActuals }) => [tw`pt-4`, $hasActuals && tw`text-cyan-darken-10`]);
export const SummarizedBookingBookingsContainer = styled.div(() => [
  tw`flex-grow relative space-y-8`
]);

/* Roster Changes */
export const ChangesContainerAnimated = styled(motion.div)(() => [tw`overflow-hidden`]);
export const ChangesButtonContainer = styled.div(() => [tw`flex justify-center`]);
export const ChangesList = styled.ul(() => [tw`pt-8 px-16 m-0`]);
export const ChangesListItem = styled.li(({ $center }) => [$center && tw`text-center`]);
export const ChangesListMarginBottomContainer = styled.div(() => [tw`mb-8`]);
export const ChangeListDivider = styled.div(() => [
  tw`bg-silver-lighten-10 h-px w-full shadow-white mb-8`
]);

/* Date divider */
/* NOTE: There also exists a (styled) component for the Event detail Schedule tab.
 * We created a different styled component for the Date divider inside the Roster
 * because in the schedule the divider elements get layouted by the wrapping table element.
 */
export const RosterItemDateDividerContainer = styled.div(() => [
  tw`relative pt-8 pb-8 mr-12 ml-12 flex -mb-8 mt-8`,
  css`
    &:before {
      content: '';
      ${tw`bg-silver-lighten-10 h-px absolute left-0 top-0 mt-8 w-full `}
    }
  `
]);
export const RosterItemDateDividerText = styled(Meta).attrs(() => ({
  as: 'p',
  $color: 'gray light'
}))(() => [`margin-top: -.625rem;`, tw`bg-white pl-8 pr-4 ml-16 relative z-1`]);

export const RosterItemDateDivider = styled.div(() => [tw`bg-silver-lighten-10 h-px`]);
